import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import _ from 'lodash';

import { GET_ASSESSMENT, GET_ASSESSMENTS, UPDATE_ASSESSMENT } from '../queries/assessment.queries';
import { sortAssessmentsNewestFirst } from '../helpers/assessment.helpers';
import { getAuthUser } from './auth.service';
import { getCurrUser } from './user.service';
import { getCurrMember } from './member.service';
import { getAllowedMemberRoles } from './user.service';
import { IUpdateAssessmentData } from '../../components/Assessment/UpdateAssessment/update-assessment.interface';

export const buildUpdateAssessment = () => {
  const user = getAuthUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(UPDATE_ASSESSMENT);

  return {
    ...rest,
    updateAssessment: (data: IUpdateAssessmentData) => mutation({
      variables: { data, _id: user?.settings.activeAssessmentId }
    })
  };
};

export const getActiveAssessmentId = () => {
  const { user } = getCurrUser();

  return _.get(user, 'settings.activeAssessmentId');
};

// Hook-in-disguise (wraps getAssessments). Resolves the active assessment's cached
// info read-only: the stored id when in the role-filtered list, else the newest.
export const getActiveAssessment = ({ requiredRoles }: { requiredRoles?: string[] } = {}): { assessment?: IAssessmentWithAllowedRoles; loading: boolean; error?: ApolloError } => {
  const activeAssessmentId = getActiveAssessmentId();
  const { assessments, loading, error } = getAssessments({ requiredRoles });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const assessment = useMemo(() => {
    const active = activeAssessmentId
      ? assessments.find((candidate) => candidate._id === activeAssessmentId)
      : undefined;
    return active || sortAssessmentsNewestFirst(assessments)[0];
  }, [activeAssessmentId, assessments]);

  return { assessment, loading, error };
};

// Single assessment by id (mirrors `getCompany`). Skips while `_id` is falsy and
// uses cache-and-network, so seed Formik with `enableReinitialize`.
export const getAssessment = (_id: string): { assessment?: IAssessment; loading: boolean; error?: ApolloError } => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error } = useQuery(GET_ASSESSMENT, {
    skip: !_id,
    variables: { _id },
    fetchPolicy: 'cache-and-network'
  });

  return { assessment: _.get(data, 'assessment'), loading, error };
};

export const getAssessments = ({requiredRoles}: {requiredRoles?: string[]} = {}): {assessments: IAssessmentWithAllowedRoles[]; loading: boolean; error?: ApolloError} => {
  const user = getAuthUser();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error } = useQuery(GET_ASSESSMENTS, {
    skip: !user,
    variables: { companyId: user?.settings.activeCompanyId },
    fetchPolicy: 'cache-and-network'
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const allowedAssessments = useRoleAllowedAssessments({ assessments: (data && data.assessments) || [], requiredRoles });

  return {
    assessments: allowedAssessments,
    loading,
    error
  };
};

export const useRoleAllowedAssessments = ({assessments = [], requiredRoles = []}: {assessments?: IAssessment[]; requiredRoles?: string[]} ): IAssessmentWithAllowedRoles[] => {
  const { member } = getCurrMember()

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const availableAssessmentsForRoles = useMemo(() => {
    if (!member) return [];
    if (!requiredRoles.length) return assessments as IAssessmentWithAllowedRoles[];

    const allowedAssessmentLookup: { [key: string]: string[] } = {};
    
    // First check super user roles - if user has required roles at super user level, all assessments are allowed
    const superUserAllowedRoles = getAllowedMemberRoles({member, roles: requiredRoles});
    const hasSuperUserAccess = superUserAllowedRoles.some(role => !role.assessmentId && !role.ageGroupId);
    
    if (hasSuperUserAccess) {
      // User has required roles at super user level - all assessments are allowed
      assessments.forEach(assessment => {
        allowedAssessmentLookup[assessment._id] = superUserAllowedRoles
          .filter(role => !role.assessmentId && !role.ageGroupId)
          .map(role => role.role);
      });
    } else {
      // Check each assessment for permissions
      for (const assessment of assessments) {
        const assessmentRoles: string[] = [];
        
        // Check if user has roles at assessment level (for assessment-specific roles)
        const assessmentAllowedRoles = getAllowedMemberRoles({member, roles: requiredRoles, assessmentId: assessment._id});
        assessmentAllowedRoles.forEach(role => {
          if (!assessmentRoles.includes(role.role)) {
            assessmentRoles.push(role.role);
          }
        });

        // Check if user has roles at any age group within this assessment
        if (assessment.ageGroupIds?.length) {
          for (const ageGroupId of assessment.ageGroupIds) {
            const ageGroupAllowedRoles = getAllowedMemberRoles({member, roles: requiredRoles, ageGroupId, assessmentId: assessment._id});
            ageGroupAllowedRoles.forEach(role => {
              if (!assessmentRoles.includes(role.role)) {
                assessmentRoles.push(role.role);
              }
            });
          }
        }
        
        // If any required roles were found for this assessment, add it to allowed assessments
        if (assessmentRoles.length > 0) {
          allowedAssessmentLookup[assessment._id] = assessmentRoles;
        }
      }
    }

    return Object.entries(allowedAssessmentLookup).map(([assessmentId, roles]) => ({...assessments.find(a => a._id === assessmentId), allowedRoles: roles} as IAssessmentWithAllowedRoles));
  }, [assessments, member, requiredRoles])

  return availableAssessmentsForRoles
};